import { ReactNode, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  formatDate,
  formatToCurrency,
  formatToNumber,
} from "../../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";
import { useGenerateRepaymentScheduleQuery } from "../../../../../redux/features/loans/loans.api";
import {
  LoanProduct,
  RequestLoanPayload,
} from "../../../../../redux/features/loans/loans.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";
import { Button } from "../../../../buttons/button";
import { LoanRepaymentScheduleGeneratedModal } from "../repaymentScheduleGeneratedModal";

export const ViewRepaymentDetails = ({
  cooperativeDetails,
  onSuccess,
  finalPayload,
  selectedLoanProduct,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
  finalPayload?: Partial<RequestLoanPayload>;
  selectedLoanProduct?: LoanProduct;
}) => {
  const [repaymentDetails, setRepaymentDetails] = useState<
    {
      title: string;
      element: ReactNode;
    }[]
  >([]);

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: repaymentDetailsData,
    isFetching: fetchingRepaymentDetailsData,
  } = useGenerateRepaymentScheduleQuery(
    {
      coop: cooperativeDetails.id,
      loanAmount: finalPayload?.loanAmount!,
      loanproductID: selectedLoanProduct?.id!,
      tenor: finalPayload?.tenor!,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (repaymentDetailsData) {
      setRepaymentDetails([
        {
          title: "Loan Product",
          element: selectedLoanProduct?.LoanName,
        },
        {
          title: "Loan Amount",
          element: formatToCurrency(repaymentDetailsData.loanAmount, {
            currency: "NGN",
            decimalPlaces: 2,
          }),
        },
        {
          title: "Loan Tenor",
          element: `${formatToNumber(repaymentDetailsData.tenor)} months`,
        },
        {
          title: "Monthly Repayment",
          element: formatToCurrency(repaymentDetailsData.monthly_payment, {
            currency: "NGN",
            decimalPlaces: 2,
          }),
        },
        {
          title: "Total Interest",
          element: formatToCurrency(repaymentDetailsData.totalInterest, {
            currency: "NGN",
            decimalPlaces: 2,
          }),
        },
        {
          title: "First Due Date",
          element: formatDate(repaymentDetailsData.FirstDueDate),
        },
        {
          title: "Maturity Date",
          element: formatDate(repaymentDetailsData.MaturityDate),
        },
        ...(finalPayload?.guarantor ?? []).map((g, idx) => ({
          title: `Guarantor ${idx + 1} Payment`,
          element: formatToCurrency(g.amount, {
            currency: "NGN",
            decimalPlaces: 2,
          }),
        })),
        {
          title: "Repayment Schedule",
          element: (
            <button
              className="w-fit text-pry-green-200"
              onClick={() => {
                handleModalOpen("viewRepaymentScheduleModal");
              }}
            >
              View Schedule
            </button>
          ),
        },
      ]);
    }
    // eslint-disable-next-line
  }, [repaymentDetailsData, finalPayload, selectedLoanProduct]);

  return (
    <>
      <div className="flex flex-col gap-4 min-h-full">
        <div className="flex flex-col gap-3 p-4 bg-light-green-bg rounded-lg">
          {fetchingRepaymentDetailsData
            ? Array.from({ length: 9 }).map((_, idx) => {
                return (
                  <div key={`loan-details-${idx}`} className="">
                    <Skeleton
                      width={"100%"}
                      height={24}
                      baseColor={SKELETON_BASE_COLOR}
                      highlightColor={SKELETON_HIGHLIGHT_COLOR}
                      className="rounded-lg"
                      containerClassName="w-full"
                    />
                  </div>
                );
              })
            : repaymentDetails.map((detail, idx) => {
                return (
                  <div
                    key={`guarantor-details-${idx}`}
                    className="flex items-center justify-between text-sm md:text-sm text-pry-dark-green-300 font-medium"
                  >
                    <div className="font-medium text-pry-dark-green-300/70">
                      {detail.title}:
                    </div>
                    <div>{detail.element}</div>
                  </div>
                );
              })}
        </div>

        <div className="pt-4 mt-auto border-t">
          <Button
            type="button"
            className=""
            text={"Accept and Proceed"}
            variation={"primary"}
            disabled={false}
            onClick={() => {
              onSuccess();
            }}
          />
        </div>
      </div>

      {repaymentDetailsData?.repayment ? (
        <LoanRepaymentScheduleGeneratedModal
          isModalOpen={modalOpen && modal === "viewRepaymentScheduleModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          schedule={repaymentDetailsData.repayment}
        />
      ) : undefined}
    </>
  );
};
